import React from 'react';
import { Box, Typography, Container, List, ListItem, ListItemText, Divider } from '@mui/material';

const sections = [
  { id: 'introduction', title: '1. Introduction' },
  { id: 'purpose', title: '2. Purpose of this Data Safety Policy' },
  { id: 'scope', title: '3. Scope' },
  { id: 'definitions', title: '4. Definitions' },
  { id: 'commitment', title: '5. Our Security Commitment' },
  { id: 'principles', title: '6. Security Principles' },
  { id: 'compliance', title: '7. Compliance Standards' },
  { id: 'contact', title: '8. Contact Information' },
  // DATA WE PROTECT
  { id: 'personal', title: '9. Personal Information' },
  { id: 'account', title: '10. Account Information' },
  { id: 'professional', title: '11. Professional Profile Data' },
  { id: 'identity', title: '12. Identity Verification Information' },
  { id: 'business', title: '13. Business Information' },
  { id: 'marketplace', title: '14. Marketplace Information' },
  { id: 'communication', title: '15. Communication Data' },
  { id: 'media', title: '16. Photos, Videos & Audio' },
  { id: 'location', title: '17. Location Information' },
  { id: 'device', title: '18. Device Information' },
  { id: 'payment', title: '19. Payment & Billing Information' },
  { id: 'analytics', title: '20. Analytics Data' },
  { id: 'developer', title: '21. Developer Data' },
  { id: 'enterprise', title: '22. Enterprise Customer Data' },
  { id: 'backup', title: '23. Backup Data' },
  // SECURITY MEASURES
  { id: 'program', title: '24. Information Security Program' },
  { id: 'governance', title: '25. Security Governance' },
  { id: 'risk', title: '26. Risk Management' },
  { id: 'access', title: '27. Access Control' },
  { id: 'auth', title: '28. Identity & Authentication' },
  { id: 'password', title: '29. Password Protection' },
  { id: 'encryption-transit', title: '30. Encryption in Transit' },
  { id: 'encryption-rest', title: '31. Encryption at Rest' },
  { id: 'key-mgmt', title: '32. Key Management' },
  { id: 'cloud', title: '33. Secure Cloud Infrastructure' },
  { id: 'network', title: '34. Network Security' },
  { id: 'firewall', title: '35. Firewall Protection' },
  { id: 'detection', title: '36. Intrusion Detection' },
  { id: 'prevention', title: '37. Intrusion Prevention' },
  { id: 'monitoring', title: '38. Security Monitoring' },
  { id: 'threat', title: '39. Threat Intelligence' },
  { id: 'endpoint', title: '40. Endpoint Protection' },
  { id: 'malware', title: '41. Malware Protection' },
  { id: 'phishing', title: '42. Anti-Phishing Protection' },
  { id: 'ddos', title: '43. DDoS Protection' },
  { id: 'logging', title: '44. Secure Logging' },
  { id: 'audits', title: '45. Security Audits' },
  // APPLICATION SECURITY
  { id: 'ssdlc', title: '46. Secure Software Development Lifecycle (SSDLC)' },
  { id: 'coding', title: '47. Secure Coding Standards' },
  { id: 'review', title: '48. Code Review' },
  { id: 'dependency', title: '49. Dependency Management' },
  { id: 'api', title: '50. API Security' },
  { id: 'mobile', title: '51. Mobile App Security' },
  { id: 'backend', title: '52. Backend Security' },
  { id: 'database', title: '53. Database Security' },
  { id: 'storage', title: '54. File Storage Security' },
  { id: 'session', title: '55. Session Security' },
  { id: 'token', title: '56. Token Security' }
];

const content = {
  introduction: `Welcome to the Filmhook Apps Data Safety & Security Policy.
At FILM-HOOK MEDIAAPPS PRIVATE LIMITED ("Filmhook Apps," "Filmhook," "Company," "we," "our," or "us"), protecting the security, privacy, confidentiality, integrity, and availability of information is one of our highest priorities. We recognize that users, businesses, production houses, media organizations, educational institutions, recruiters, advertisers, marketplace participants, developers, and enterprise customers trust us with their information when using our platform. We are committed to maintaining that trust by implementing comprehensive administrative, technical, organizational, and physical safeguards to protect data throughout its lifecycle.

Filmhook Apps is a professional networking and digital ecosystem designed for the entertainment, media, creative, business, and related industries. Our platform enables professionals, organizations, and businesses to connect, communicate, collaborate, recruit talent, showcase portfolios, conduct business transactions, promote opportunities, and access various digital services in a secure environment.

This Data Safety & Security Policy explains how Filmhook Apps protects information, manages security risks, safeguards digital assets, responds to security incidents, and continuously improves its security practices. It also describes the responsibilities shared by Filmhook Apps and our users in maintaining a safe and secure platform.

This policy should be read together with our Privacy Policy, Terms of Service, Community Guidelines, Cookie Policy, and any product-specific security notices that may apply to particular Filmhook Apps services.`,

  purpose: `The purpose of this Data Safety & Security Policy is to clearly explain how Filmhook Apps protects information and maintains a secure environment for all users of the platform.
This policy aims to:
- Explain our approach to information security.
- Describe the categories of data we protect.
- Outline the safeguards used to protect user information.
- Explain how security incidents are identified, investigated, and managed.
- Describe user responsibilities in maintaining account security.
- Demonstrate our commitment to protecting confidentiality, integrity, and availability of information.
- Promote transparency regarding our security practices.
- Support compliance with applicable data protection and cybersecurity laws.
- Strengthen trust between Filmhook Apps and our users, customers, partners, and stakeholders.
- Establish consistent security practices across all Filmhook Apps products and services.

This policy is intended to help users understand our security framework while promoting responsible and secure use of the Filmhook Apps platform.`,

  scope: `This Data Safety & Security Policy applies to all products, services, websites, mobile applications, APIs, communication services, marketplaces, business tools, enterprise solutions, and future offerings operated by FILM-HOOK MEDIAAPPS PRIVATE LIMITED under the Filmhook Apps brand, unless a separate security notice expressly applies.
This policy applies to:
- Registered users, visitors accessing our websites, and creative professionals (actors, artists, technicians, journalists, and media professionals).
- Businesses, organizations, production houses, casting agencies, recruitment agencies, and educational institutions.
- Marketplace buyers and sellers, advertisers, sponsors, and enterprise customers.
- Developers using approved APIs or integrations.
- Vendors, contractors, consultants, and service providers who process information on our behalf.
- Employees and authorized personnel with access to Filmhook Apps systems.

The policy applies to information processed through mobile applications, official websites, web portals, APIs, customer support systems, communication services, marketplace services, business management tools, verification services, enterprise services, and future digital services operated by Filmhook Apps, regardless of the country from which they are accessed, subject to applicable local laws.`,

  definitions: `For the purposes of this Data Safety & Security Policy, the following terms have the meanings described below:
- Account: A registered Filmhook Apps user profile used to access our services.
- Administrator: An authorized individual responsible for managing organizational or business accounts.
- Business Account: An account representing a company, organization, institution, agency, production house, or other business entity.
- Data: Any information collected, stored, transmitted, processed, or otherwise handled by Filmhook Apps.
- Device: Any smartphone, tablet, computer, wearable device, television, or other electronic device capable of accessing Filmhook Apps services.
- Enterprise Customer: An organization that uses Filmhook Apps products or services for commercial, organizational, or institutional purposes.
- Incident: An event that may compromise the confidentiality, integrity, or availability of systems, services, or information.
- Marketplace: Any Filmhook Apps feature that facilitates buying, selling, renting, promoting, or advertising products or services.
- Personal Information: Information relating to an identified or identifiable individual, as defined under applicable data protection laws.
- Security Incident: An actual or suspected event involving unauthorized access, disclosure, alteration, destruction, misuse, or loss of information or systems.
- Service Provider: A third-party organization that performs services on behalf of Filmhook Apps under appropriate contractual and security obligations.
- User: Any individual or organization accessing or using Filmhook Apps services.`,

  commitment: `Protecting user information is fundamental to our business and to the trust placed in us by our community.
Filmhook Apps is committed to:
- Protecting the confidentiality of information.
- Maintaining the integrity and accuracy of information.
- Ensuring the availability and resilience of our systems.
- Preventing unauthorized access to user information.
- Continuously improving our security controls.
- Monitoring emerging cybersecurity threats.
- Applying secure software development practices.
- Protecting users against fraud, abuse, and malicious activity.
- Responding promptly to security incidents.
- Supporting compliance with applicable privacy and cybersecurity laws.
- Promoting a culture of security awareness throughout our organization.

Security is integrated into our products, operational processes, infrastructure, and business practices. While no online service can guarantee absolute security, we strive to implement reasonable and appropriate safeguards designed to protect the information entrusted to us.`,

  principles: `Filmhook Apps follows core security principles that guide the design, operation, and continuous improvement of our services:
- 6.1 Confidentiality: We implement safeguards to help ensure that information is accessible only to authorized individuals and systems.
- 6.2 Integrity: We seek to protect information from unauthorized modification, corruption, or destruction and maintain the accuracy and reliability of our systems and data.
- 6.3 Availability: We design our infrastructure and operational processes to support reliable access to our services, while recognizing that interruptions may occur due to maintenance, technical issues, or circumstances beyond our control.
- 6.4 Accountability: We maintain internal governance processes to help ensure that security responsibilities are clearly defined, monitored, and periodically reviewed.
- 6.5 Risk-Based Security: We evaluate security risks on an ongoing basis and implement safeguards that are appropriate to the nature of the information we process and the services we provide.
- 6.6 Defense in Depth: We employ multiple layers of administrative, technical, and organizational controls to reduce the likelihood and impact of security threats.
- 6.7 Continuous Improvement: We regularly review and improve our security practices to respond to technological developments, emerging threats, industry best practices, and changes in legal or regulatory requirements.`,

  compliance: `Filmhook Apps seeks to operate in accordance with applicable privacy, cybersecurity, consumer protection, and information security laws and regulations in the jurisdictions where we provide services.
Our security program is informed by recognized industry practices and may consider frameworks such as:
- General Data Protection Regulation (GDPR), where applicable.
- California Consumer Privacy Act (CCPA) and California Privacy Rights Act (CPRA), where applicable.
- India's Digital Personal Data Protection Act (DPDP Act), where applicable.
- National Institute of Standards and Technology (NIST) Cybersecurity Framework.
- ISO/IEC 27001 information security management principles.
- OWASP Secure Development Guidelines.
- Applicable payment security standards where payment processing is supported.

References to these standards do not imply formal certification unless expressly stated by Filmhook Apps.`,

  contact: `If you have questions, concerns, or requests regarding this Data Safety & Security Policy or the security of Filmhook Apps services, you may contact us through our official support channels:

FILM-HOOK MEDIAAPPS PRIVATE LIMITED
Filmhook Apps – Security & Privacy Team
Website: https://www.filmhookapps.com
Support Email: support@filmhookapp.com
Security Email: security@filmhookapp.com

Business Address:
FILM-HOOK MEDIAAPPS PRIVATE LIMITED
1st Floor, 'A, MAIN ROAD, 3, Mysore Rd, near Indian Oil Chetti Petrol Bunk, North Layout, Metro Lay Out, Nayanda Halli, Bengaluru, Karnataka 560039

We may request reasonable identity verification before responding to certain requests to protect the security and privacy of our users.`,

  personal: `Filmhook Apps protects personal information that identifies, relates to, describes, or can reasonably be linked to an individual.
Personal information may include:
- Full name, display name, and username.
- Profile and cover photographs.
- Date of birth, gender (optional), email address, and mobile number.
- Residential or business address (where applicable), country, state, and city.
- Language preferences and biography.
- Professional information, skills, education, experience, and social media links.
- Verification status.

Personal information is protected using administrative, technical, and organizational safeguards appropriate to its sensitivity.`,

  account: `Every Filmhook Apps account contains information required to identify and manage user access.
Protected account information may include:
- User ID, username, registered email address, and registered mobile number.
- Account creation date and login history.
- Password (stored in encrypted or hashed form) and authentication credentials.
- Account recovery settings, session information, and security preferences.
- Notification preferences and account status.
- Subscription and verification status.

Access to account information is restricted to authorized processes and personnel with a legitimate business need.`,

  professional: `Filmhook Apps is a professional networking platform that enables users to build industry profiles and portfolios.
Professional profile data may include:
- Profession, job title, department, skills, and specializations.
- Years of experience, education, certifications, awards, and filmography.
- Portfolio assets, resume or Curriculum Vitae (CV), and languages spoken.
- Professional biography, employment history, freelance experience, and business affiliations.
- Professional achievements and availability for work.

Users control the information displayed publicly through available privacy and profile settings.`,

  identity: `To improve trust and reduce impersonation or fraudulent activity, Filmhook Apps may protect identity verification information submitted voluntarily by users during applicable verification processes.
Such information may include:
- Government-issued identification documents and selfie photographs.
- Identity verification images and professional identity documents.
- Business registration certificates and employment verification documents.
- Verification status, timestamps, and records.

Verification information is processed only for legitimate verification, fraud prevention, security, compliance, or legal purposes and is protected using enhanced security measures.`,

  business: `Filmhook Apps supports businesses, production houses, agencies, organizations, educational institutions, and enterprise customers.
Protected business information may include:
- Business name, registered company name, and business logo.
- Business description and industry category.
- Business registration number and GST information (where applicable).
- Official website, official email addresses, and office contact information.
- Business addresses, business licenses, and business verification status.
- Business profile content and advertisements.
- Company representatives.

Business information is protected in accordance with this policy and applicable contractual obligations.`,

  marketplace: `Filmhook Apps provides marketplace features that allow users and businesses to buy, sell, rent, promote, or advertise products and services.
Marketplace information may include:
- Product listings, service listings, rental listings, and property listings.
- Pricing, product descriptions, images, and availability.
- Seller information, buyer inquiries, and order history.
- Marketplace communications and delivery information (where applicable).
- Business transactions, reviews, and ratings.

Marketplace information is protected to help ensure secure and trustworthy transactions.`,

  communication: `Filmhook Apps enables communication between users and organizations through various communication features.
Protected communication data may include:
- Direct messages, group messages, and business messages.
- Customer support conversations and media shared within conversations.
- Message timestamps, message delivery status, and read receipts.
- Call metadata and notification history.

Where communication services use encryption, Filmhook Apps implements appropriate safeguards designed to protect communications during transmission.`,

  media: `Users may upload multimedia content to their profiles, portfolios, marketplace listings, business pages, and other services.
Protected multimedia content may include:
- Profile and cover photographs.
- Portfolio images, videos, and audio files.
- Reels, stories, and event media.
- Marketplace images and business promotional media.
- Documents uploaded as media.

Filmhook Apps stores multimedia content using secure storage systems and applies access controls appropriate to the service.`,

  location: `Depending on user permissions and device settings, Filmhook Apps may protect location-related information.
Location information may include:
- GPS coordinates and approximate location.
- City, state, country, and time zone.
- Device location, marketplace location, event location, and business location.

Users may enable or disable location permissions through their device settings, subject to feature limitations.`,

  device: `To maintain service security, compatibility, and performance, Filmhook Apps protects certain technical information about devices accessing our services.
Device information may include:
- Device model, manufacturer, and operating system.
- Device language, browser type, and app version.
- Screen resolution and device identifiers.
- IP address, network provider, and time zone.
- Crash reports, diagnostic information, and security logs.

This information assists with troubleshooting, fraud prevention, and service optimization.`,

  payment: `Where applicable, Filmhook Apps protects payment and billing information used for subscriptions, premium services, marketplace transactions, or business services.
Protected information may include:
- Billing name and billing address.
- Invoice details and subscription records.
- Payment confirmations and transaction identifiers.
- Tax information and refund records.

Filmhook Apps generally does not store complete payment card information unless necessary for lawful operational purposes and protected using appropriate safeguards. Payments may also be processed through authorized third-party payment service providers.`,

  analytics: `Filmhook Apps protects operational and analytical information used to understand service performance and improve user experience.
Analytics information may include:
- App usage statistics, session duration, and screen navigation.
- Feature usage and device performance.
- Error reports and crash diagnostics.
- Search interactions and engagement metrics.
- Network performance and service availability metrics.

Analytics information is used to improve reliability, performance, and user experience while implementing appropriate privacy protections.`,

  developer: `Where Filmhook Apps provides APIs, SDKs, integrations, or developer services, we protect information associated with approved developers and technical integrations.
Developer information may include:
- Developer account details and API credentials.
- Integration settings and application identifiers.
- Technical logs and usage statistics.
- Security audit records and support requests.
- Access permissions and developer communications.

Access to developer systems is controlled through authentication, authorization, and security monitoring mechanisms.`,

  enterprise: `Filmhook Apps may process information on behalf of enterprise customers using our business services.
Enterprise customer data may include:
- Organization profiles and administrative account information.
- Employee account information and recruitment data.
- Business communications and marketplace information.
- Advertising campaigns and customer support records.
- Billing records, enterprise reports, and service configurations.
- Administrative settings.

Enterprise data is protected according to contractual commitments, organizational security practices, and applicable laws.`,

  backup: `To support business continuity, disaster recovery, operational resilience, and service reliability, Filmhook Apps may maintain protected backup copies of certain information.
Backup data may include:
- User account, profile, and business information.
- Marketplace records and system configurations.
- Security logs, operational databases, and service metadata.

Backups are protected using appropriate access controls, encryption where applicable, retention schedules, and secure recovery procedures. Backup information is retained only for as long as reasonably necessary to support legitimate operational, legal, and security requirements.`,

  program: `FILM-HOOK MEDIAAPPS PRIVATE LIMITED ("Filmhook Apps") maintains a comprehensive Information Security Program designed to protect the confidentiality, integrity, availability, and resilience of the information entrusted to us.
Information security is integrated into our business operations, software development lifecycle, infrastructure management, and organizational governance.

Our Information Security Program is designed to:
- Protect user information against unauthorized access, disclosure, alteration, destruction, or loss.
- Safeguard business systems and digital infrastructure.
- Maintain service availability and operational resilience.
- Reduce cybersecurity risks through proactive security measures.
- Support compliance with applicable privacy and security laws.
- Promote continuous improvement of security controls.

The program applies to mobile applications, web applications, backend services, APIs, cloud infrastructure, databases, marketplace services, messaging systems, verification services, and customer support.`,

  governance: `Security governance establishes the policies, responsibilities, and oversight necessary to ensure that security is managed consistently across Filmhook Apps.
Security governance includes:
- Security policies, standards, and operational procedures.
- Risk management and compliance monitoring.
- Incident management and continuous improvement.
- Executive oversight.

Roles and Responsibilities:
- Executive Leadership: Supporting security initiatives, resource allocation, and risk review.
- Engineering Teams: Secure software development, testing, and vulnerability remediation.
- Operations Teams: Infrastructure security, system monitoring, and disaster recovery.
- Customer Support: Verification during interactions and escalation of security incidents.
- Employees: Adhering to internal security policies and completing training.`,

  risk: `Filmhook Apps maintains an ongoing process to identify, evaluate, prioritize, and manage risks affecting our systems, users, and business operations.
- Types of Risks Evaluated: Cybersecurity threats, unauthorized access, data breaches, software vulnerabilities, human error, fraud, third-party risks, and regulatory changes.
- Risk Assessment: Security risks are evaluated based on likelihood, impact, data sensitivity, and legal obligations.
- Risk Mitigation: Implementation of technical security controls, training, monitoring, software updates, and incident response planning.`,

  access: `Access to systems and information is granted only to the extent necessary for authorized business purposes.
- Principle of Least Privilege: Employees, contractors, and service providers receive access appropriate to their responsibilities.
- Role-Based Access Control (RBAC): Access permissions are assigned based on predefined roles and reviewed periodically.
- Administrative Access: Privileged access is restricted, logged, and requires strong authentication.
- Access Reviews: Periodic reviews are conducted to update permissions, revoke access for role changes, and disable inactive accounts.`,

  auth: `Authentication mechanisms verify that users accessing Filmhook Apps are authorized account holders.
- Authentication Methods: Username/email/mobile number, passwords, One-Time Passwords (OTP), tokens, and device recognition.
- Session Authentication: Session expiration, timeout limits, secure session identifiers, and invalidation after logout.
- Authentication Monitoring: Systems monitor for failed attempts, unusual behavior, suspicious devices, and potential credential compromises.`,

  password: `Users are encouraged to create strong, unique passwords that contain uppercase/lowercase letters, numbers, and special characters.
- Password Storage: Filmhook Apps does not store passwords in plain text; they are protected using strong cryptographic hashing algorithms.
- Password Reset: Reset procedures require identity verification before allowing password updates.
- User Responsibilities: Keep passwords confidential, change compromised passwords immediately, and report unauthorized access.`,

  'encryption-transit': `Filmhook Apps uses secure communication protocols (SSL/TLS 1.2 or higher) designed to protect information during transmission between users, applications, and our servers.
This reduces the risk of interception and tampering for authentication, API calls, direct messages, marketplace transactions, and billing transactions.`,

  'encryption-rest': `FILM-HOOK MEDIAAPPS PRIVATE LIMITED implements appropriate safeguards to protect stored information ("at rest").
- Protected Data: Account info, professional profiles, verification records, marketplace transactions, billing details, and backup data.
- Storage Safeguards: Database encryption, encrypted volumes, secure backups, and restricted access privileges.`,

  'key-mgmt': `Cryptographic keys are managed throughout their lifecycle to ensure database and volume security.
Key management practices include secure key generation, storage, access control, key rotation, and secure key retirement or destruction.`,

  cloud: `Filmhook Apps hosts applications, databases, and assets in secure, world-class cloud environments (e.g., GCP or AWS).
These facilities feature 24/7 physical security, biometric access controls, redundant hardware, automated backups, and vulnerability scanning.`,

  network: `Network security controls protect infrastructure and information from unauthorized access.
Measures include network segmentation, secure routing, access control lists (ACLs), virtual private networking, and configuration audits.`,

  firewall: `Firewalls control incoming and outgoing network traffic, blocking unauthorized connections, restricting unnecessary services, and supporting incident monitoring.`,

  detection: `Intrusion detection capabilities identify suspicious activities such as unusual authentication attempts, malicious network traffic, unauthorized access, or configuration anomalies.`,

  prevention: `Preventive controls complement intrusion detection by blocking malicious traffic, rate-limiting API requests, enforcing access restrictions, and isolating backend services.`,

  monitoring: `We continuously monitor infrastructure, login systems, application health, API activity, and security logs to preserve operational stability and security.`,

  threat: `We gather threat intelligence to remain informed of emerging cybersecurity trends, public vulnerabilities, and attack vectors, assisting in system hardening and patch prioritization.`,

  endpoint: `Devices used to administer Filmhook Apps systems are protected with device authentication, operating system updates, endpoint protection software, and disk encryption.`,

  malware: `We deploy anti-malware protections, file integrity monitoring, secure deployments, and software updates to protect systems from malicious software.`,

  phishing: `We implement email verification protocols, authentication monitoring, and user awareness guidance to reduce the risk of phishing and spoofing attacks.`,

  ddos: `Traffic filtering, rate limiting, and network redundancy are deployed to mitigate Distributed Denial-of-Service (DDoS) attacks and ensure system availability.`,

  logging: `System and security logs record authentication events, administrative actions, API access, and configuration changes to support incident audits and compliance.`,

  audits: `We conduct periodic security assessments, infrastructure reviews, access audits, and policy evaluations to strengthen Filmhook's overall security posture.`,

  ssdlc: `Filmhook Apps integrates security into the Software Development Lifecycle (SDLC).
Our Secure SDLC (SSDLC) involves security reviews during requirements gathering, secure architecture design, security testing during coding, and post-deployment maintenance.`,

  coding: `Developers adhere to secure coding standards including input validation, secure error handling, minimizing privileges, and mitigating risks like injection and session hijacking.`,

  review: `Peer code reviews are conducted before deployment to verify application logic, security controls, performance, and code maintainability.`,

  dependency: `We manage third-party libraries and frameworks by regularly monitoring for vulnerabilities, updating supported versions, and deprecating unused packages.`,

  api: `Our APIs use authentication, role authorization, secure communication, request validation, rate limiting, and access logging to prevent unauthorized data exposure.`,

  mobile: `Mobile application security includes secure local storage, secure API communication, session management, and regular app store security updates.`,

  backend: `Backend servers are protected with authentication controls, service isolation, automated patching, logging, and infrastructure hardening.`,

  database: `Databases are secured using authentication, strict access privileges, encryption at rest, secure replication, and automated activity logging.`,

  storage: `User-uploaded files, media, and documents are stored using access controls, file validation, security permissions, and backup replication.`,

  session: `Sessions are managed using secure session identifiers, automatic inactivity timeouts, logout invalidation, and protection against session hijacking.`,

  token: `API tokens and authentication keys are protected through secure generation, strict expiration windows, cryptographic signatures, and transmission over SSL/TLS.`
};

const DataSafety = () => {
  return (
    <Box sx={{ py: { xs: 4, sm: 8, md: 12 }, minHeight: '80vh' }}>
      <Container maxWidth="md">
        <Box
          sx={{
            background: 'var(--bg-card)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: '1px solid var(--border-color)',
            borderRadius: '24px',
            p: { xs: 2.5, sm: 4, md: 7 },
            boxShadow: '0 16px 48px rgba(0,0,0,0.2)',
          }}
        >
          <Typography variant="subtitle2" sx={{ color: 'var(--text-muted)', mb: 2, fontSize: { xs: '0.75rem', sm: '0.875rem' } }} textAlign="right">
            Last Updated: {new Date().toLocaleDateString('en-GB')}
          </Typography>

          <Typography variant="h3" fontWeight={800} gutterBottom sx={{ color: 'var(--text-heading)', textAlign: 'center', mb: 1, fontSize: { xs: '1.8rem', sm: '2.5rem', md: '3rem' } }}>
            Data Safety & Security Policy
          </Typography>
          <Typography variant="h6" sx={{ color: '#5dade2', textAlign: 'center', mb: 3, fontWeight: 500, letterSpacing: '0.5px', fontSize: { xs: '0.9rem', sm: '1.1rem', md: '1.25rem' } }}>
            FILM-HOOK MEDIAAPPS PRIVATE LIMITED
          </Typography>
          <Box sx={{ width: 80, height: 3, background: 'linear-gradient(90deg, #07a4f5, #5dade2)', mx: 'auto', mb: 6, borderRadius: '2px' }} />

          {sections.map(({ id, title }, idx) => (
            <Box key={id} sx={{ mb: 6 }}>
              <Typography variant="h5" fontWeight={700} gutterBottom sx={{ color: 'var(--text-heading)', mb: 2, fontSize: { xs: '1.2rem', sm: '1.35rem', md: '1.5rem' } }}>
                {title}
              </Typography>
              
              {content[id]?.split('\n\n').map((paragraph, pIdx) => {
                if (paragraph.trim().startsWith('- ')) {
                  return (
                    <List key={pIdx} sx={{ pl: 2, mb: 2 }}>
                      {paragraph.split('\n').map((item, itemIdx) => (
                        <ListItem key={itemIdx} alignItems="flex-start" disableGutters sx={{ py: 0.5 }}>
                          <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#5dade2', mr: 2, mt: '10px', flexShrink: 0 }} />
                          <ListItemText
                            primary={
                              <Typography variant="body1" sx={{ color: 'var(--text-secondary)', lineHeight: 1.8, fontSize: { xs: '0.88rem', md: '1rem' } }}>
                                {item.replace('- ', '')}
                              </Typography>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  );
                }
                return (
                  <Typography key={pIdx} variant="body1" sx={{ color: 'var(--text-secondary)', lineHeight: 1.8, textAlign: 'justify', mb: 2, fontSize: { xs: '0.88rem', md: '1rem' } }}>
                    {paragraph}
                  </Typography>
                );
              })}
              
              {idx < sections.length - 1 && (
                <Divider sx={{ mt: 5, borderColor: 'var(--border-subtle)' }} />
              )}
            </Box>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default DataSafety;
